import React, { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
} from "date-fns";
import {
  HiOutlineCalendar,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineCollection,
} from "react-icons/hi";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import { gbState } from "../../components/shared/GlobalState";
import placeholderImage from "../../images/event-placeholder-image.jpg";

const EventCalendarChild = () => {
  let navigate = useNavigate();
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [selectedDetailEvent, setSelectedDetailEvent] = useState(null);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      const small = window.innerWidth < 768;
      console.log("Window width:", window.innerWidth);
      console.log("Is small screen:", small);
      setIsSmallScreen(small);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");
    // let urlLink = `${process.env.REACT_APP_API_URL}/event/${user.church_id}`;
    let urlLink = `${process.env.REACT_APP_API_URL}/event/zipcode/${user.church_zipcode}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios(config)
      .then(function (response) {
        const oldData = response.data.events;
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data._id,
            title: data.title,
            description: data.description,
            event_start_date: data?.event_start_date,
            view_event_start_date: data?.event_start_date
              ? dayjs(data?.event_start_date).format("DD-MM-YYYY") +
                " " +
                data?.event_start_time
              : "",
            event_end_date: data?.event_end_date,
            view_event_end_date: data?.event_end_date
              ? dayjs(data?.event_end_date).format("DD-MM-YYYY") +
                " " +
                data?.event_end_time
              : "",
          };
        });

        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        errorLogApi(err, "EventListChild", "/events");
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, []);

  const getDaysInMonth = (date) => {
    const start = startOfWeek(startOfMonth(date));
    const end = endOfWeek(endOfMonth(date));
    return eachDayOfInterval({ start, end });
  };

  const days = getDaysInMonth(currentDate);

  const nextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const previousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const getEventsForDate = (date) => {
    return data.filter((event) => {
      const eventStartDate = new Date(event.event_start_date);
      const eventEndDate = event.event_end_date
        ? new Date(event.event_end_date)
        : eventStartDate;
      const currentDate = new Date(date);

      // Reset time parts to compare only dates
      eventStartDate.setHours(0, 0, 0, 0);
      eventEndDate.setHours(0, 0, 0, 0);
      currentDate.setHours(0, 0, 0, 0);

      // Check if the current date falls within the event's date range
      return currentDate >= eventStartDate && currentDate <= eventEndDate;
    });
  };

  const getEventShade = (dayEvents, day) => {
    if (dayEvents.length === 0) return "";

    // Return bg-blue-500 if there are any events on this day
    return "bg-blue-500 text-white";
  };

  const EventDetailModal = ({ event, onClose }) => {
    const eventDate = new Date(event.event_start_date);
    const eventsForDate = data.filter((e) => {
      const date = new Date(e.event_start_date);
      return (
        date.getDate() === eventDate.getDate() &&
        date.getMonth() === eventDate.getMonth() &&
        date.getFullYear() === eventDate.getFullYear()
      );
    });

    return (
      <div className="flex fixed inset-0 z-50 justify-center items-center p-0 bg-black bg-opacity-50 sm:p-4">
        <div className="overflow-y-auto relative p-4 w-full max-w-3xl h-full bg-white sm:h-auto sm:rounded-lg sm:p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>

          {selectedDetailEvent ? (
            // Detailed Event View
            <div>
              <button
                onClick={() => setSelectedDetailEvent(null)}
                className="flex items-center mb-4 text-sm text-blue-600 hover:text-blue-800"
              >
                <svg
                  className="mr-1 w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Events List
              </button>

              <div className="flex flex-col gap-6 sm:flex-row">
                <div className="overflow-hidden flex-shrink-0 w-full h-48 rounded-lg sm:w-48">
                  {selectedDetailEvent.image ? (
                    <img
                      crossOrigin="anonymous"
                      src={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${selectedDetailEvent.image}`}
                      alt={selectedDetailEvent.title}
                      className="object-cover w-full h-full"
                      onError={(e) => {
                        e.target.src = placeholderImage;
                        // "https://placehold.co/128x128/e2e8f0/64748b?text=No+Image";
                      }}
                    />
                  ) : (
                    <img
                      src={placeholderImage}
                      alt={selectedDetailEvent.title}
                      className="object-cover w-full h-full"
                    />
                  )}
                </div>

                <div className="flex-grow">
                  <div className="flex flex-col gap-2 justify-between mb-4 sm:flex-row sm:items-start">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {selectedDetailEvent.title}
                    </h2>
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full w-fit ${
                        selectedDetailEvent.event_type?.value === "Public"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {selectedDetailEvent.event_type?.label || "N/A"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="flex items-center text-gray-600">
                        <svg
                          className="mr-2 w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div>
                          <p className="font-medium">Date & Time</p>
                          <p>
                            {format(
                              new Date(selectedDetailEvent.event_start_date),
                              "MMMM d, yyyy"
                            )}
                          </p>
                          <p>
                            {selectedDetailEvent.event_start_time} -{" "}
                            {selectedDetailEvent.event_end_time}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="mr-2 w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                        <div>
                          <p className="font-medium">Event Type</p>
                          <p>
                            {selectedDetailEvent.event_type?.label || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center text-gray-600">
                        <svg
                          className="mr-2 w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div>
                          <p className="font-medium">Sponsorship</p>
                          <div className="flex gap-2 items-center">
                            <span
                              className={`
                                px-3 py-1 text-sm rounded-full flex items-center gap-1
                                ${
                                  selectedDetailEvent.sponsorship_status
                                    ?.value === "Open"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }
                              `}
                            >
                              {selectedDetailEvent.sponsorship_status?.value ===
                              "Open" ? (
                                <>
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                                    />
                                  </svg>
                                  {selectedDetailEvent.sponsorship_status
                                    ?.label || "Open"}
                                </>
                              ) : (
                                <>
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                  </svg>
                                  {selectedDetailEvent.sponsorship_status
                                    ?.label || "Closed"}
                                </>
                              )}
                            </span>
                            {selectedDetailEvent.amount && (
                              <span className="text-sm font-medium text-gray-700">
                                ${selectedDetailEvent.amount}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      {selectedDetailEvent?.church_id?.church_name && (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="mr-2 w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                          <div>
                            <p className="font-medium">Church</p>
                            <p>{selectedDetailEvent.church_id.church_name}</p>
                          </div>
                        </div>
                      )}

                      {selectedDetailEvent.event_contact_number && (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="mr-2 w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                            />
                          </svg>
                          <div>
                            <p className="font-medium">Contact</p>
                            <p>{selectedDetailEvent.event_contact_number}</p>
                          </div>
                        </div>
                      )}

                      {(selectedDetailEvent.event_location ||
                        selectedDetailEvent.zipcode) && (
                        <div className="flex items-center text-gray-600">
                          <svg
                            className="mr-2 w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <div>
                            <p className="font-medium">Location</p>
                            {selectedDetailEvent.event_location && (
                              <p>{selectedDetailEvent.event_location}</p>
                            )}
                            {selectedDetailEvent.zipcode && (
                              <p className="text-sm text-gray-500">
                                ZIP: {selectedDetailEvent.zipcode}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buttons section */}
                  <div className="flex flex-col gap-4 items-stretch pt-6 pb-4 sm:flex-row sm:items-center">
                    {selectedDetailEvent.sponsorship_status?.value ===
                      "Open" && (
                      <button
                        onClick={() => {
                          navigate("/sponsorship-application", {
                            state: selectedDetailEvent,
                          });
                        }}
                        className="flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md border border-transparent transition-colors duration-150 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        <svg
                          className="mr-2 w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Apply as Sponsor
                      </button>
                    )}

                    <button
                      onClick={() => {
                        navigate("/event-detail", {
                          state: selectedDetailEvent,
                        });
                      }}
                      className="flex justify-center items-center px-4 py-2 text-sm font-medium text-blue-600 bg-white rounded-md border border-blue-600 transition-colors duration-150 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg
                        className="mr-2 w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      View Event Details
                    </button>
                  </div>

                  {/* Description section */}
                  <div className="pt-6">
                    <h3 className="mb-2 text-lg font-medium text-gray-800">
                      Description
                    </h3>
                    <p className="text-gray-600">
                      {selectedDetailEvent.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Events List View - Update the container for better mobile view
            <div className="h-full sm:h-auto">
              <h2 className="mb-6 text-xl font-bold text-gray-800 sm:text-2xl">
                Events on {format(eventDate, "MMMM d, yyyy")}
              </h2>

              <div className="space-y-4 max-h-[calc(100vh-12rem)] sm:max-h-[60vh] overflow-y-auto pr-2">
                {eventsForDate.map((evt) => (
                  <div
                    key={evt.id}
                    className="flex gap-4 p-4 rounded-lg border transition-colors duration-200 hover:bg-gray-50"
                  >
                    {/* Event Image */}
                    <div className="overflow-hidden flex-shrink-0 w-32 h-32 rounded-lg">
                      {evt.image ? (
                        <img
                          crossOrigin="anonymous"
                          src={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${evt.image}`}
                          alt={evt.title}
                          className="object-cover w-full h-full"
                          onError={(e) => {
                            e.target.src =
                              "https://placehold.co/128x128/e2e8f0/64748b?text=No+Image";
                          }}
                        />
                      ) : (
                        <img
                          src={placeholderImage}
                          alt={evt.title}
                          className="object-cover w-full h-full"
                        />
                      )}
                    </div>

                    {/* Event Details */}
                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-800">
                            {evt.title.length > 30 ? (
                              <span title={evt.title}>
                                {evt.title.substring(0, 30)}...
                              </span>
                            ) : (
                              evt.title
                            )}
                          </h3>
                          {evt.event_type?.label && (
                            <p className="text-sm text-gray-600">
                              {evt.event_type.label}
                            </p>
                          )}
                          {evt.church_id?.church_name && (
                            <p className="flex items-center mt-1 text-sm text-gray-600">
                              <svg
                                className="mr-1 w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                              {evt.church_id.church_name}
                            </p>
                          )}
                        </div>
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${
                            evt.event_type?.value === "Public"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {evt.event_type?.label || "N/A"}
                        </span>
                      </div>

                      <div className="space-y-3 text-sm text-gray-600">
                        <p className="flex items-center">
                          <svg
                            className="mr-2 w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {evt.event_start_time} - {evt.event_end_time}
                        </p>

                        <p className="flex items-center">
                          <svg
                            className="mr-2 w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          {evt.event_location}
                        </p>

                        <button
                          onClick={() => setSelectedDetailEvent(evt)}
                          className="px-4 py-2 mt-2 text-sm font-medium text-blue-600 rounded-md border border-blue-600 transition-colors duration-150 hover:bg-blue-50"
                        >
                          More Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Add this style to handle events display based on screen size
  const eventDisplayStyle = `
    @media (max-width: 768px) {
      .event-list {
        display: none;
      }
      .event-dot {
        display: block;
      }
    }
    @media (min-width: 769px) {
      .event-list {
        display: block;
      }
      .event-dot {
        display: none;
      }
    }
  `;

  return (
    <div className="p-6 w-full bg-white rounded-lg shadow-sm">
      <style>{eventDisplayStyle}</style>
      {/* Calendar Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <HiOutlineCalendar className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {format(currentDate, "MMMM yyyy")}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              <HiOutlineCollection className="inline mr-1 w-4 h-4" />
              Manage and view your events
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={previousMonth}
            className="flex justify-center items-center p-2 text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
            aria-label="Previous month"
          >
            <HiChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextMonth}
            className="flex justify-center items-center p-2 text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
            aria-label="Next month"
          >
            <HiChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Week days header */}
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div
            key={day}
            className="p-2 text-sm font-semibold text-center text-gray-600 border-b border-gray-200"
          >
            {isSmallScreen ? day.charAt(0) : day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((day, index) => {
          const isCurrentMonth = isSameMonth(day, currentDate);
          const dayEvents = getEventsForDate(day);
          const hasEvents = dayEvents.length > 0;
          const isToday = isSameDay(day, new Date());

          return (
            <div
              key={index}
              className={`relative p-2 min-h-[80px] sm:min-h-[120px] transition-all duration-200
                ${!isCurrentMonth ? "bg-gray-50" : ""}
                ${
                  hasEvents
                    ? "text-white bg-blue-500 hover:bg-blue-600"
                    : "hover:bg-gray-50"
                }
                ${hasEvents ? "cursor-pointer" : ""}
                ${isToday ? "ring-2 ring-inset ring-blue-500" : ""}`}
              onClick={() => {
                if (dayEvents.length > 0) {
                  setSelectedEvent(dayEvents[0]);
                  setIsModalOpen(true);
                }
              }}
            >
              <div className="flex justify-between items-center mb-1">
                <span
                  className={`inline-flex items-center justify-center w-7 h-7 rounded-full
                    ${
                      isCurrentMonth
                        ? hasEvents
                          ? "font-semibold text-white bg-white bg-opacity-25"
                          : isToday
                          ? "text-white bg-blue-500"
                          : "text-gray-700"
                        : "text-gray-400"
                    }
                  `}
                >
                  {format(day, "d")}
                </span>
                {isCurrentMonth && hasEvents && (
                  <span className="px-2 py-1 text-xs font-medium text-white bg-white bg-opacity-25 rounded-full">
                    {dayEvents.length}{" "}
                    {dayEvents.length === 1 ? "event" : "events"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedEvent(null);
            setSelectedDetailEvent(null);
          }}
        />
      )}
    </div>
  );
};

export default EventCalendarChild;
